import { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import { subscribeOptionsChanged, notifyOptionsChanged } from "@/lib/optionsBus";

/**
 * Smart Combobox with creatable entries (no Popover/Command — plain absolute dropdown).
 */
export default function SmartCombobox({ value, onChange, optionType, placeholder = "Select…", testId }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const mounted = useRef(true);

  const fetchOptions = async () => {
    try {
      const { data } = await api.get(`/options/${optionType}`);
      if (mounted.current) setOptions(data || []);
    } catch (e) { console.error(`Failed to fetch ${optionType} options:`, e); }
  };

  useEffect(() => {
    mounted.current = true;
    fetchOptions();
    const unsub = subscribeOptionsChanged(optionType, () => fetchOptions());
    return () => { mounted.current = false; unsub(); };
    // eslint-disable-next-line
  }, [optionType]);

  useEffect(() => {
    function onDoc(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false); setSearch("");
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const pick = (v) => { onChange(v); setOpen(false); setSearch(""); };

  const handleCreate = async (val) => {
    const v = val.trim();
    if (!v) return;
    try { await api.post(`/options/${optionType}`, { value: v }); } catch (e) { console.error(`Failed to save ${optionType} option:`, e); }
    setOptions((prev) => (prev.includes(v) ? prev : [...prev, v].sort()));
    notifyOptionsChanged(optionType);
    pick(v);
  };

  const filtered = options.filter((o) => o.toLowerCase().includes(search.toLowerCase()));
  const showCreate = search.trim() && !options.some((o) => o.toLowerCase() === search.trim().toLowerCase());

  return (
    <div ref={wrapperRef} className="relative">
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen((o) => !o)}
        className="w-full justify-between font-normal h-10 text-sm"
        data-testid={testId}
      >
        <span className={cn(!value && "text-muted-foreground")}>
          {value || placeholder}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-md shadow-lg overflow-hidden">
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  if (showCreate) handleCreate(search);
                  else if (filtered.length === 1) pick(filtered[0]);
                }
              }}
              placeholder="Search or type new…"
              className="w-full h-8 px-2 text-sm border border-[#E2E8F0] rounded outline-none focus:border-[#0055FF]"
              data-testid={`${testId}-input`}
            />
          </div>
          <div className="max-h-56 overflow-auto py-1">
            {filtered.map((opt) => (
              <button
                type="button"
                key={opt}
                onClick={() => pick(opt)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2"
              >
                <Check className={cn("h-4 w-4", value === opt ? "opacity-100 text-[#0055FF]" : "opacity-0")} />
                {opt}
              </button>
            ))}
            {showCreate && (
              <button
                type="button"
                onClick={() => handleCreate(search)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2 text-[#0055FF]"
                data-testid={`${testId}-create`}
              >
                <Plus className="h-4 w-4" /> Add "{search}"
              </button>
            )}
            {filtered.length === 0 && !showCreate && (
              <div className="px-3 py-2 text-sm text-muted-foreground">No results.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
